import React, { useState } from 'react';
import { Modal, Button, Input, Alert, Spin } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';

/**
 * MachineResetControl - botão "Zerar máquina" + modal de confirmação.
 *
 * Usado no drawer do PWA (MachineDailyStatusButton) e na tela de Perdas do
 * painel admin (DRY: um único fluxo de zeramento nos dois lugares).
 *
 * O modal busca o preview (saldo da máquina + tanque estimado) e deixa claro
 * que esses valores serão registrados como PERDA do turno. Após confirmar,
 * a API grava o MachineReset (auditado) e os cálculos do dia recomeçam do
 * zero para a máquina.
 *
 * Props:
 * - machine: { machine_id, code } máquina a zerar (formato do daily-balance).
 * - onDone: callback após zerar com sucesso (ex.: recarregar o balanço).
 * - size: tamanho do botão (default 'large' — touch-friendly no PWA).
 * - block: botão ocupa a largura toda (default false).
 */
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

const MachineResetControl = ({ machine, onDone, size = 'large', block = false }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [observation, setObservation] = useState('');

  /** Abre o modal e busca os saldos atuais — os valores gravados como perda. */
  const openModal = async () => {
    setOpen(true);
    setPreview(null);
    setObservation('');
    setPreviewLoading(true);
    try {
      const res = await api.operation.previewMachineReset({ machine_id: machine.machine_id });
      setPreview(res.data);
    } catch (err) {
      showErrorMessage(err?.response?.data?.error || 'Erro ao calcular o zeramento.');
      setOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmReset = async () => {
    setSaving(true);
    try {
      await api.operation.createMachineReset({
        machine_id: machine.machine_id,
        observation: observation || undefined
      });
      showSuccessMessage(`Máquina ${machine.code} zerada — perda registrada.`);
      setOpen(false);
      if (onDone) onDone();
    } catch (err) {
      showErrorMessage(err?.response?.data?.error || 'Erro ao zerar máquina.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        danger
        icon={<ClearOutlined />}
        size={size}
        block={block}
        onClick={openModal}
        aria-label={`Zerar máquina ${machine.code} e registrar a perda do turno`}
      >
        Zerar máquina
      </Button>

      <Modal
        title={`Zerar máquina ${machine.code}`}
        open={open}
        onCancel={() => setOpen(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)}>Cancelar</Button>,
          <Button
            key="ok"
            type="primary"
            danger
            loading={saving}
            disabled={previewLoading || !preview}
            onClick={confirmReset}
          >
            Zerar e registrar perda
          </Button>,
        ]}
      >
        {previewLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><Spin /></div>
        ) : preview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Alert
              type="warning"
              showIcon
              message="Estes valores serão registrados como PERDA do turno"
              description="A máquina e o tanque voltam a zero e os cálculos do dia recomeçam. A ação fica na auditoria."
            />

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1, textAlign: 'center', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 8px' }}>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Saldo da máquina</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#c1281a' }}>{fmt(preview.machineBalance)} L</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 8px' }}>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Tanque (estimado)</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#c1281a' }}>{fmt(preview.tankEstimate)} L</div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Observação (opcional)</div>
              <Input.TextArea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                autoSize={{ minRows: 2 }}
                placeholder="Ex.: troca de turno, conta não fechou"
                aria-label="Observação do zeramento"
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default MachineResetControl;
